// features/timesheet/timesheet.slice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import i18n from '../../../translation/i18n';
import {
  addTimesheetWorkingHoursApi,
  getTimesheetWorkingHoursApi,
  getEmployeeWorkingHoursApi,
  deleteTimesheetWorkingHoursApi,
  updateTimesheetWorkingHoursApi,
  getTimesheetReportApi,
  AddTimesheetRequest,
  AddTimesheetResponse,
  GetTimesheetRequest,
  GetTimesheetResponse,
  GetEmployeeWorkingHoursRequest,
  GetEmployeeWorkingHoursResponse,
  DeleteTimesheetRequest,
  DeleteTimesheetResponse,
  UpdateTimesheetRequest,
  UpdateTimesheetResponse,
  GetTimesheetReportRequest,
  GetTimesheetReportResponse,
  TimesheetWorkingHoursV2
} from './timesheet.service';

export const addTimesheetWorkingHours = createAsyncThunk<
  AddTimesheetResponse,
  AddTimesheetRequest,
  { rejectValue: string }
>(
  'timesheet/addTimesheetWorkingHours',
  async (data, { rejectWithValue }) => {
    try {
      const response = await addTimesheetWorkingHoursApi(data);
      toast.success(response.message || (i18n.t('TIMESHEET_SAVED_SUCCESSFULLY') as string));
      return response;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        (i18n.t('FAILED_TO_SAVE_TIMESHEET') as string);
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const getTimesheetWorkingHours = createAsyncThunk<
  GetTimesheetResponse,
  GetTimesheetRequest,
  { rejectValue: string }
>(
  'timesheet/getTimesheetWorkingHours',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getTimesheetWorkingHoursApi(params);
      return response;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        (i18n.t('FAILED_TO_FETCH_TIMESHEET') as string);
      return rejectWithValue(message);
    }
  }
);

export const getEmployeeWorkingHours = createAsyncThunk<
  GetEmployeeWorkingHoursResponse,
  GetEmployeeWorkingHoursRequest,
  { rejectValue: string }
>(
  'timesheet/getEmployeeWorkingHours',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getEmployeeWorkingHoursApi(params);
      return response;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        (i18n.t('FAILED_TO_FETCH_EMPLOYEE_WORKING_HOURS') as string);
      return rejectWithValue(message);
    }
  }
);

export const deleteTimesheetWorkingHours = createAsyncThunk<
  DeleteTimesheetResponse,
  DeleteTimesheetRequest,
  { rejectValue: string }
>(
  'timesheet/deleteTimesheetWorkingHours',
  async (data, { rejectWithValue }) => {
    try {
      const response = await deleteTimesheetWorkingHoursApi(data);
      if (response.statusCode === 200) {
        toast.success(i18n.t('WORKING_HOUR_DELETED_SUCCESSFULLY') as string);
      } else {
        toast.error(i18n.t('SOMETHING_WENT_WRONG') as string);
      }
      return response;
    } catch (error: any) {
      toast.error(i18n.t('SOMETHING_WENT_WRONG') as string);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        (i18n.t('FAILED_TO_DELETE_TIMESHEET_ENTRY') as string);
      return rejectWithValue(message);
    }
  }
);

export const updateTimesheetWorkingHours = createAsyncThunk<
  UpdateTimesheetResponse,
  UpdateTimesheetRequest,
  { rejectValue: string }
>(
  'timesheet/updateTimesheetWorkingHours',
  async (data, { rejectWithValue }) => {
    try {
      const response = await updateTimesheetWorkingHoursApi(data);
      if (response.statusCode === 200) {
        toast.success(i18n.t('TIMESHEET_WORKING_HOUR_UPDATED') as string);
      } else {
        toast.error(i18n.t('SOMETHING_WENT_WRONG') as string);
      }
      return response;
    } catch (error: any) {
      toast.error(i18n.t('SOMETHING_WENT_WRONG') as string);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        (i18n.t('FAILED_TO_UPDATE_TIMESHEET') as string);
      return rejectWithValue(message);
    }
  }
);

export const getTimesheetReport = createAsyncThunk<
  GetTimesheetReportResponse,
  GetTimesheetReportRequest,
  { rejectValue: string }
>(
  'timesheet/getTimesheetReport',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getTimesheetReportApi(params);
      return response;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        (i18n.t('FAILED_TO_FETCH_TIMESHEET_REPORT') as string);
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

interface TimesheetState {
  loading: boolean;
  error: string | null;
  savedTimesheet: AddTimesheetResponse | null;
  timesheetData: TimesheetWorkingHoursV2[];
  fetchLoading: boolean;
  fetchError: string | null;
  employeeWorkingHours: any[];
  employeeWorkingHoursLoading: boolean;
  employeeWorkingHoursError: string | null;
  deleteLoading: boolean;
  deleteError: string | null;
  reportData: any[];
  reportLoading: boolean;
  reportError: string | null;
  numberOfPages: number;
  totalCount: number;
  reportNumberOfPages: number;
}

const initialState: TimesheetState = {
  loading: false,
  error: null,
  savedTimesheet: null,
  timesheetData: [],
  fetchLoading: false,
  fetchError: null,
  employeeWorkingHours: [],
  employeeWorkingHoursLoading: false,
  employeeWorkingHoursError: null,
  deleteLoading: false,
  deleteError: null,
  reportData: [],
  reportLoading: false,
  reportError: null,
  numberOfPages: 0,
  totalCount: 0,
  reportNumberOfPages: 0,
};

const timesheetSlice = createSlice({
  name: 'timesheet',
  initialState,
  reducers: {
    clearTimesheetError: (state) => {
      state.error = null;
    },
    clearSavedTimesheet: (state) => {
      state.savedTimesheet = null;
    },
    clearTimesheetData: (state) => {
      state.timesheetData = [];
      state.fetchError = null;
    },
    resetTimesheetData: (state) => {
      state.timesheetData = [];
      state.savedTimesheet = null;
      state.error = null;
      state.fetchError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addTimesheetWorkingHours.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTimesheetWorkingHours.fulfilled, (state, action) => {
        state.loading = false;
        state.savedTimesheet = action.payload;
      })
      .addCase(addTimesheetWorkingHours.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to save timesheet';
      })
      .addCase(getTimesheetWorkingHours.pending, (state) => {
        state.fetchLoading = true;
        state.fetchError = null;
      })
      .addCase(getTimesheetWorkingHours.fulfilled, (state, action) => {
        state.fetchLoading = false;
        // Extract timeSheetWorkingHoursV2List from the response
        
        // Try to access the data with different possible structures
        const payloadAny = action.payload as any;
        const data = action.payload.timeSheetWorkingHoursV2List || 
                     payloadAny?.response?.timeSheetWorkingHoursV2List || 
                     payloadAny?.data?.timeSheetWorkingHoursV2List ||
                     [];
        
        state.timesheetData = data;
        state.numberOfPages = action.payload.numberOfPages || 0;
        state.totalCount = action.payload.totalCount || 0;
      })
      .addCase(getTimesheetWorkingHours.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.payload || 'Failed to fetch timesheet';
      })
      .addCase(getEmployeeWorkingHours.pending, (state) => {
        state.employeeWorkingHoursLoading = true;
        state.employeeWorkingHoursError = null;
      })
      .addCase(getEmployeeWorkingHours.fulfilled, (state, action) => {
        state.employeeWorkingHoursLoading = false;
        const payloadAny = action.payload as any;
        // Extract workingHours array from response object
        let data = [];
        if (payloadAny?.workingHours && Array.isArray(payloadAny.workingHours)) {
          data = payloadAny.workingHours;
        } else if (payloadAny?.response?.workingHours && Array.isArray(payloadAny.response.workingHours)) {
          data = payloadAny.response.workingHours;
        } else if (Array.isArray(payloadAny)) {
          data = payloadAny;
        } else if (action.payload.response && Array.isArray(action.payload.response)) {
          data = action.payload.response;
        }
        state.employeeWorkingHours = data;
      })
      .addCase(getEmployeeWorkingHours.rejected, (state, action) => {
        state.employeeWorkingHoursLoading = false;
        state.employeeWorkingHoursError = action.payload || 'Failed to fetch employee working hours';
      })
      .addCase(deleteTimesheetWorkingHours.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteTimesheetWorkingHours.fulfilled, (state, action) => {
        state.deleteLoading = false;
      })
      .addCase(deleteTimesheetWorkingHours.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload || 'Failed to delete timesheet entry';
      })
      .addCase(getTimesheetReport.pending, (state) => {
        state.reportLoading = true;
        state.reportError = null;
      })
      .addCase(getTimesheetReport.fulfilled, (state, action) => {
        state.reportLoading = false;
        const payloadAny = action.payload as any;
        const data = action.payload.response || payloadAny?.data || [];
        state.reportData = data;
        state.reportNumberOfPages = action.payload.numberOfPages || 0;
      })
      .addCase(getTimesheetReport.rejected, (state, action) => {
        state.reportLoading = false;
        state.reportError = action.payload || 'Failed to fetch timesheet report';
      });
  },
});

export const { clearTimesheetError, clearSavedTimesheet, clearTimesheetData, resetTimesheetData } = timesheetSlice.actions;
export default timesheetSlice.reducer;
